import type { RaspiDevice, Camera, Chamber } from '../types/raspi';
import type { Experiment } from '../types/experiment';
import { useAuthStore } from '../store/authStore';

// Generic API functions
async function apiRequest<T>(endpoint: string, options?: RequestInit): Promise<T> {
  // Use relative URL - let nginx handle the proxy
  const url = `/api${endpoint}`;
  
  const { token } = useAuthStore.getState();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const config: RequestInit = {
    ...options,
    headers: {
      ...headers,
      ...(options?.headers as Record<string, string> || {}),
    },
  };

  try {
    const response = await fetch(url, config);
    
    if (response.status === 401 || response.status === 403) {
      const { token, expireSession, logout } = useAuthStore.getState();
      // Only flag as "expired" if there was a token to begin with — otherwise
      // it's just an unauthenticated viewer hitting a privileged endpoint.
      if (token) {
        expireSession();
      } else {
        logout();
      }
      throw new Error('Authentication required');
    }
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`API request failed for ${url}:`, error);
    throw error;
  }
}

// RASPI API functions
export async function getAllRaspis(): Promise<Record<string, RaspiDevice>> {
  const data = await apiRequest<any[]>('/data/raspis');
  const raspis = data.map(transformRaspiFromApi);
  return raspis.reduce((acc, raspi) => {
    acc[raspi.raspi_id] = raspi;
    return acc;
  }, {} as Record<string, RaspiDevice>);
}

// Camera API functions
export async function getAllCameras(): Promise<Record<string, Camera>> {
  const data = await apiRequest<any[]>('/data/cameras');
  const cameras = data.map(transformCameraFromApi);
  return cameras.reduce((acc, camera) => {
    acc[camera.camera_id] = camera;
    return acc;
  }, {} as Record<string, Camera>);
}

export async function updateCamera(cameraId: string, data: Partial<Camera>): Promise<boolean> {
  try {
    await apiRequest('/data/cameras/update', {
      method: 'POST',
      body: JSON.stringify({ camera_id: cameraId, data }),
    });
    return true;
  } catch (error) {
    console.error('Error updating camera:', error);
    return false;
  }
}

export async function deleteCamera(cameraId: string): Promise<boolean> {
  try {
    await apiRequest('/data/cameras/delete', {
      method: 'POST',
      body: JSON.stringify({ camera_id: cameraId }),
    });
    return true;
  } catch (error) {
    console.error('Error deleting camera:', error);
    return false;
  }
}

// Chamber API functions
export async function getAllChambers(): Promise<Record<string, Chamber>> {
  const data = await apiRequest<any[]>('/data/chambers');
  const chambers = data.map(transformChamberFromApi);
  return chambers.reduce((acc, chamber) => {
    acc[chamber.chamber_id] = chamber;
    return acc;
  }, {} as Record<string, Chamber>);
}

export async function deleteChamber(chamberId: string): Promise<boolean> {
  try {
    await apiRequest('/data/chambers/delete', {
      method: 'POST',
      body: JSON.stringify({ chamber_id: chamberId }),
    });
    return true;
  } catch (error) {
    console.error('Error deleting chamber:', error);
    return false;
  }
}

// Experiment API functions
export async function getAllExperiments(): Promise<Experiment[]> {
  const data = await apiRequest<any[]>('/data/experiments');
  return data.map(transformExperimentFromApi);
}

export interface ExperimentMeasurements {
  time: number[];                    // seconds from the first measurement
  pressure: (number | null)[];       // control-sensor pressure per measurement
  temperature: (number | null)[];    // control-sensor temperature per measurement
  count?: number;
}

// Real measured control-sensor series for an experiment, read from its CSV by
// the hub. Used to plot the true "Measured Points" (the old ones were just the
// expected profile values sliced to the counter).
export async function getExperimentMeasurements(expId: string): Promise<ExperimentMeasurements> {
  return apiRequest<ExperimentMeasurements>(`/experiments/${encodeURIComponent(expId)}/measurements`);
}

export async function createExperiment(experiment: Omit<Experiment, 'exp_id'>): Promise<Experiment> {
  const data = await apiRequest<any>('/data/experiments/create', {
    method: 'POST',
    body: JSON.stringify(transformExperimentToApi(experiment)),
  });
  return transformExperimentFromApi(data);
}

export async function updateExperiment(expId: string, experiment: Partial<Experiment>): Promise<boolean> {
  try {
    await apiRequest('/data/experiments/update', {
      method: 'POST',
      body: JSON.stringify({ exp_id: expId, data: transformExperimentToApi(experiment) }),
    });
    return true;
  } catch (error) {
    console.error('Error updating experiment:', error);
    return false;
  }
}

// Transformation functions - API to Frontend
function transformRaspiFromApi(data: any): RaspiDevice {
  return {
    raspi_id: data.raspi_id,
    raspi_ip: data.raspi_ip,
    raspi_mac: data.raspi_mac || '',
    raspi_software_version: data.raspi_software_version || '',
    raspi_status: data.raspi_status || 'DISCONNECTED',
    raspi_health_status: data.raspi_health_status || 'CHECK',
    raspi_health_message: data.raspi_health_message || '',
    raspi_alert: data.raspi_alert || 'NO',
    raspi_alert_description: data.raspi_alert_description || '',
    chamber_id: data.chamber_id || null,
    camera_ids: Array.isArray(data.camera_ids) ? data.camera_ids : [],
    exp_id: data.exp_id || null,
    raspi_total_ram_gb: data.raspi_total_ram_gb || null,
    raspi_free_ram_gb: data.raspi_free_ram_gb || null,
    raspi_total_sd_gb: data.raspi_total_sd_gb || null,
    raspi_free_sd_gb: data.raspi_free_sd_gb || null,
    last_seen: data.last_seen ? new Date(data.last_seen) : new Date(),
    last_update: data.last_update ? new Date(data.last_update) : new Date(),
  };
}

function transformCameraFromApi(data: any): Camera {
  return {
    camera_id: data.camera_id,
    raspi_id: data.raspi_id,
    camera_gauge_id: data.camera_gauge_id || '',
    camera_led_id: data.camera_led_id || '',
    camera_status: data.camera_status || 'SLEEP',
    camera_alert: data.camera_alert || 'NO',
    camera_alert_description: data.camera_alert_description || '',
    image_dir_path: data.image_dir_path || null,
    camera_image_counter: data.camera_image_counter || 0,
    camera_image_total: data.camera_image_total || 0,
    camera_model: data.camera_model || 'PEN',
    camera_resolution: data.camera_resolution || '1920X1080',
    last_update: data.last_update ? new Date(data.last_update) : new Date(),
  };
}

function transformChamberFromApi(data: any): Chamber {
  return {
    chamber_id: data.chamber_id,
    raspi_id: data.raspi_id,
    chamber_arduino_port: data.chamber_arduino_port || '',
    baudrate: data.baudrate || 9600,
    chamber_software_version: data.chamber_software_version || '',
    arduino_hardware: data.arduino_hardware || 'Unknown',
    chamber_status: data.chamber_status || 'STOP',
    chamber_alert: data.chamber_alert || 'NO',
    chamber_alert_description: data.chamber_alert_description || '',
    csv_file_path: data.csv_file_path || null,
    chamber_meas_counter: data.chamber_meas_counter || 0,
    chamber_meas_total: data.chamber_meas_total || 0,
    last_update: data.last_update ? new Date(data.last_update) : new Date(),
  };
}

function transformExperimentFromApi(data: any): Experiment {
  return {
    exp_id: data.exp_id,
    test_id: data.test_id,
    profile_id: data.profile_id,
    num_profiles: data.num_profiles,
    start_time: data.start_time ? new Date(data.start_time) : null,
    end_time: data.end_time ? new Date(data.end_time) : null,
    exp_status_time: data.exp_status_time ? new Date(data.exp_status_time) : null,
    exp_status: data.exp_status || 'NEW',
    raspi_id: data.raspi_id || null,
    chamber_id: data.chamber_id || null,
    chamber_enabled: data.chamber_enabled || false,
    cameras_enabled: Array.isArray(data.cameras_enabled) ? data.cameras_enabled : [],
    exp_alert: data.exp_alert || 'NO',
    exp_alert_description: data.exp_alert_description || '',
    exp_notes: data.exp_notes || '',
    exp_time_total: data.exp_time_total || 0,
    exp_time_counter: data.exp_time_counter || 0,
    exp_points_total: data.exp_points_total || 0,
    exp_points_counter: data.exp_points_counter || 0,
    exp_expect_total: data.exp_expect_total || 0,
    exp_expect_counter: data.exp_expect_counter || 0,
    pressure_steps: Array.isArray(data.pressure_steps) ? data.pressure_steps : [],
    temperature_steps: Array.isArray(data.temperature_steps) ? data.temperature_steps : [],
    time_steps: Array.isArray(data.time_steps) ? data.time_steps : [],
    measurement_intervals: data.measurement_intervals || 10,
    time_interval_steps: Array.isArray(data.time_interval_steps) ? data.time_interval_steps : [],
    profile_file_path: data.profile_file_path || null,
  };
}

// Transformation functions - Frontend to API
function transformExperimentToApi(experiment: Partial<Experiment>): any {
  const transformed: any = {};
  
  if (experiment.test_id !== undefined) transformed.test_id = experiment.test_id;
  if (experiment.profile_id !== undefined) transformed.profile_id = experiment.profile_id;
  if (experiment.num_profiles !== undefined) transformed.num_profiles = experiment.num_profiles;
  if (experiment.start_time !== undefined) transformed.start_time = experiment.start_time?.toISOString();
  if (experiment.end_time !== undefined) transformed.end_time = experiment.end_time?.toISOString();
  if (experiment.exp_status_time !== undefined) transformed.exp_status_time = experiment.exp_status_time?.toISOString();
  if (experiment.exp_status !== undefined) transformed.exp_status = experiment.exp_status;
  if (experiment.raspi_id !== undefined) transformed.raspi_id = experiment.raspi_id;
  if (experiment.chamber_id !== undefined) transformed.chamber_id = experiment.chamber_id;
  if (experiment.chamber_enabled !== undefined) transformed.chamber_enabled = experiment.chamber_enabled;
  if (experiment.cameras_enabled !== undefined) transformed.cameras_enabled = experiment.cameras_enabled;
  if (experiment.exp_alert !== undefined) transformed.exp_alert = experiment.exp_alert;
  if (experiment.exp_alert_description !== undefined) transformed.exp_alert_description = experiment.exp_alert_description;
  if (experiment.exp_notes !== undefined) transformed.exp_notes = experiment.exp_notes;
  if (experiment.exp_time_total !== undefined) transformed.exp_time_total = experiment.exp_time_total;
  if (experiment.exp_time_counter !== undefined) transformed.exp_time_counter = experiment.exp_time_counter;
  if (experiment.exp_points_total !== undefined) transformed.exp_points_total = experiment.exp_points_total;
  if (experiment.exp_points_counter !== undefined) transformed.exp_points_counter = experiment.exp_points_counter;
  if (experiment.exp_expect_total !== undefined) transformed.exp_expect_total = experiment.exp_expect_total;
  if (experiment.exp_expect_counter !== undefined) transformed.exp_expect_counter = experiment.exp_expect_counter;
  if (experiment.pressure_steps !== undefined) transformed.pressure_steps = experiment.pressure_steps;
  if (experiment.temperature_steps !== undefined) transformed.temperature_steps = experiment.temperature_steps;
  if (experiment.time_steps !== undefined) transformed.time_steps = experiment.time_steps;
  if (experiment.measurement_intervals !== undefined) transformed.measurement_intervals = experiment.measurement_intervals;
  if (experiment.time_interval_steps !== undefined) transformed.time_interval_steps = experiment.time_interval_steps;
  if (experiment.profile_file_path !== undefined) transformed.profile_file_path = experiment.profile_file_path;
  
  return transformed;
}

// Preferences API functions
export async function getHubPreferences(): Promise<any> {
  return apiRequest('/preferences');
}

export async function updateHubPreferences(preferences: any): Promise<boolean> {
  await apiRequest('/system/preferences', {
    method: 'PUT',
    body: JSON.stringify(preferences),
    headers: { 'Content-Type': 'application/json' },
  });
  return true;
}

// System Health API functions
export async function checkHubHealth(): Promise<any> {
  return apiRequest('/system/health');
}

// Logs API functions
export async function getHubLogs(lines: number = 1000, level?: string): Promise<any> {
  const params = new URLSearchParams();
  params.append('lines', lines.toString());
  if (level) params.append('level', level);
  return apiRequest(`/system/logs?${params.toString()}`);
}

export async function getRaspiLogs(raspiId: string, lines: number = 1000, level?: string): Promise<any> {
  const params = new URLSearchParams();
  params.append('lines', lines.toString());
  if (level) params.append('level', level);
  return apiRequest(`/system/raspi_logs?raspi_id=${raspiId}&${params.toString()}`);
}

// Command API functions
export async function refreshCameras(raspiId: string): Promise<boolean> {
  try {
    await apiRequest(`/commands/refresh_cameras`, { 
      method: 'POST',
      body: JSON.stringify({ raspi_id: raspiId })
    });
    return true;
  } catch (error) {
    console.error('Error refreshing cameras:', error);
    return false;
  }
}

export async function refreshChamber(raspiId: string): Promise<boolean> {
  try {
    await apiRequest(`/commands/refresh_chamber`, { 
      method: 'POST',
      body: JSON.stringify({ raspi_id: raspiId })
    });
    return true;
  } catch (error) {
    console.error('Error refreshing chamber:', error);
    return false;
  }
}

export async function sendExperimentCommand(expId: string, command: string, additionalData?: Record<string, any>): Promise<boolean> {
  try {
    const payload: any = { exp_id: expId, command };
    if (additionalData) {
      Object.assign(payload, additionalData);
    }
    
    await apiRequest(`/commands/experiment_command`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return true;
  } catch (error) {
    console.error('Error sending experiment command:', error);
    return false;
  }
}

export async function updatePumpTime(raspiId: string, pumpTime: number, direction: 'positive' | 'negative'): Promise<boolean> {
  try {
    await apiRequest(`/commands/update_pump_time`, {
      method: 'POST',
      body: JSON.stringify({ raspi_id: raspiId, pump_time: pumpTime, direction }),
    });
    return true;
  } catch (error) {
    console.error('Error updating pump time:', error);
    return false;
  }
}

export async function calibrateSensors(raspiId: string, calibrationType: 'reset' | 'offset'): Promise<boolean> {
  try {
    await apiRequest(`/commands/calibrate_sensors`, {
      method: 'POST',
      body: JSON.stringify({ raspi_id: raspiId, calibration_type: calibrationType }),
    });
    return true;
  } catch (error) {
    console.error('Error sending sensor calibration command:', error);
    return false;
  }
}

export async function setChamberReadiness(raspiId: string, enabled: boolean): Promise<boolean> {
  try {
    await apiRequest(`/commands/set_chamber_readiness`, {
      method: 'POST',
      body: JSON.stringify({ raspi_id: raspiId, enabled }),
    });
    return true;
  } catch (error) {
    console.error('Error setting chamber readiness:', error);
    return false;
  }
}

export async function disconnectRaspi(raspiId: string): Promise<boolean> {
  try {
    await apiRequest(`/commands/disconnect_raspi`, {
      method: 'POST',
      body: JSON.stringify({ raspi_id: raspiId }),
    });
    return true;
  } catch (error) {
    console.error('Error disconnecting RASPI:', error);
    return false;
  }
}



export async function restartSoftware(raspiId: string): Promise<boolean> {
  try {
    await apiRequest(`/commands/restart_software`, { 
      method: 'POST',
      body: JSON.stringify({ raspi_id: raspiId })
    });
    return true;
  } catch (error) {
    console.error('Error restarting software:', error);
    return false;
  }
}

export async function sendCameraStreamCommand(raspiId: string, cameraId: string, start: boolean): Promise<boolean> {
  try {
    await apiRequest(`/commands/camera_stream`, { 
      method: 'POST',
      body: JSON.stringify({ 
        raspi_id: raspiId, 
        camera_id: cameraId, 
        start: start 
      })
    });
    return true;
  } catch (error) {
    console.error('Error sending camera stream command:', error);
    return false;
  }
}

export async function sendLedControlCommand(raspiId: string, cameraId: string, ledId: string, action: 'toggle' | 'on' | 'off' = 'toggle'): Promise<boolean> {
  try {
    await apiRequest(`/commands/led_control`, { 
      method: 'POST',
      body: JSON.stringify({ 
        raspi_id: raspiId, 
        camera_id: cameraId, 
        led_id: ledId,
        action: action
      })
    });
    return true;
  } catch (error) {
    console.error('Error sending LED control command:', error);
    return false;
  }
} 

// Device Management API functions (SSH-based, no MQTT)
export async function updateRaspiSoftware(raspiId: string): Promise<any> {
  return apiRequest('/commands/update_software', {
    method: 'POST',
    body: JSON.stringify({ raspi_id: raspiId })
  });
}

export async function restartRaspiSoftware(raspiId: string): Promise<any> {
  return apiRequest('/commands/restart_software', {
    method: 'POST',
    body: JSON.stringify({ raspi_id: raspiId })
  });
}

export async function rebootRaspi(raspiId: string): Promise<any> {
  return apiRequest('/commands/reboot_raspi', {
    method: 'POST',
    body: JSON.stringify({ raspi_id: raspiId })
  });
}

export async function loadRaspiData(raspiId: string): Promise<any> {
  return apiRequest('/commands/load_raspi_data', {
    method: 'POST',
    body: JSON.stringify({ raspi_id: raspiId })
  });
}

export async function clearRaspiFiles(raspiId: string): Promise<any> {
  return apiRequest('/commands/clear_raspi_files', {
    method: 'POST',
    body: JSON.stringify({ raspi_id: raspiId })
  });
} 

export async function getRaspiSoftwareVersionCheck(raspiId: string): Promise<{ current_version: string, new_version: string, update_needed: boolean }> {
  return apiRequest(`/software/version_check?raspi_id=${raspiId}`);
}

export async function getArduinoAvailableVersion(): Promise<{ available_version: string }> {
  return apiRequest('/preferences/arduino_version');
}

// Help API functions
export async function getHelpDocs(): Promise<any> {
  return apiRequest('/help/docs');
}

export async function getHelpDoc(filename: string): Promise<{ filename: string; title: string; content: string }> {
  return apiRequest(`/help/docs/${encodeURIComponent(filename)}`);
}

export async function searchHelpDocs(query: string): Promise<any> {
  return apiRequest(`/help/search?query=${encodeURIComponent(query)}`);
}

export { apiRequest }; 